import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, User, Sparkles, Heart, Zap, TreePine, Network, Flame, ArrowRight } from 'lucide-react';

interface SessionTypeSelectorProps {
  onSelectType: (type: 'individual' | 'group') => void;
  categoryName: string;
}

export const SessionTypeSelector: React.FC<SessionTypeSelectorProps> = ({ onSelectType, categoryName }) => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Golden background */}
      <div className="absolute inset-0">
        <div className="w-full h-full bg-gradient-to-br from-golden-dark/20 to-golden-light/20"></div>
      </div>
      
      {/* Golden overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10"></div>
      
      {/* Animated Background Elements - Golden Themed */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Left Side Particles */}
        <div className="absolute top-1/4 left-1/6 w-3 h-3 bg-primary rounded-full animate-twinkle opacity-80"></div>
        <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-secondary rounded-full animate-float opacity-60"></div>
        <div className="absolute bottom-1/3 left-1/5 w-4 h-4 bg-accent/70 rounded-full animate-breathe opacity-50"></div>
        <div className="absolute top-2/3 left-1/3 w-1 h-1 bg-primary rounded-full animate-twinkle opacity-70"></div>
        
        {/* Right Side Particles */}
        <div className="absolute top-1/4 right-1/6 w-3 h-3 bg-secondary rounded-full animate-float-delayed opacity-80"></div>
        <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-primary rounded-full animate-twinkle opacity-70"></div>
        <div className="absolute bottom-1/3 right-1/5 w-4 h-4 bg-secondary/70 rounded-full animate-breathe opacity-60"></div>
        <div className="absolute top-2/3 right-1/3 w-1 h-1 bg-accent rounded-full animate-float opacity-50"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Header Section */}
        <div className="text-center mb-16 animate-slideInUp">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 mb-6">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Select Your Experience</span>
          </div>
          
          <h1 className="text-5xl font-bold bg-gradient-hero bg-clip-text text-transparent tracking-tight mb-6">
            Choose Your <span className="bg-gradient-hero bg-clip-text text-transparent">Journey</span>
          </h1>
          <p className="text-xl text-muted-foreground/90 font-light max-w-4xl mx-auto leading-relaxed">
            Select the meditation path that resonates with your current needs for{' '}
            <span className="font-semibold text-primary">{categoryName}</span>
          </p>
        </div>

        {/* Session Type Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Individual Journey Card */}
          <Card className="group relative overflow-hidden border-2 backdrop-blur-xl transition-all duration-700 hover:scale-105 hover:-translate-y-2 border-primary/40 shadow-lg hover:shadow-primary/20">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: 'url(lovable-uploads/6226c4fe-699d-4c50-a0b4-9014005d2c43.png)' }}
            ></div>
            {/* Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-primary/20 group-hover:from-background/90 group-hover:via-background/80 transition-all duration-700"></div>
            {/* Golden Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-700 bg-gradient-to-br from-primary/30 to-primary-glow/30"></div>
            
            <CardHeader className="relative z-10 text-center pb-8">
              <div className="w-24 h-24 mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-primary rounded-full blur-lg opacity-50 group-hover:opacity-80 transition-opacity duration-500"></div>
                <div className="relative w-full h-full border-2 border-primary/40 rounded-full flex items-center justify-center bg-background group-hover:border-primary/80 transition-all duration-500">
                  <User className="w-10 h-10 text-primary group-hover:scale-110 transition-transform duration-500" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold mb-3 text-foreground drop-shadow-sm">
                Individual Journey
              </CardTitle>
              <CardDescription className="text-lg leading-relaxed text-muted-foreground drop-shadow-sm">
                A personal, introspective experience crafted uniquely for your inner exploration
              </CardDescription>
            </CardHeader>

            <CardContent className="relative z-10 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Sparkles className="w-5 h-5 flex-shrink-0 text-primary" />
                  <span>Deeply personalized meditation content</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <TreePine className="w-5 h-5 flex-shrink-0 text-accent" />
                  <span>Mystical, nature-inspired guidance</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Heart className="w-5 h-5 flex-shrink-0 text-secondary" />
                  <span>Sacred space for self-discovery</span>
                </div>
              </div>

              <Button 
                onClick={(e) => {
                  console.log("🎯 Individual button clicked in SessionTypeSelector");
                  e.stopPropagation();
                  onSelectType('individual');
                }}
                className="w-full h-14 text-lg font-semibold rounded-xl hover:scale-[1.02] transition-all duration-500 shadow-lg"
                variant="default"
              >
                Begin Individual Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>

          {/* Group Journey Card */}
          <Card className="group relative overflow-hidden border-2 backdrop-blur-xl transition-all duration-700 hover:scale-105 hover:-translate-y-2 border-accent/40 shadow-lg hover:shadow-accent/20">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: 'url(lovable-uploads/c5e4a7dc-87c9-4468-8d90-f7232e65bf29.png)' }}
            ></div>
            {/* Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-accent/20 group-hover:from-background/90 group-hover:via-background/80 transition-all duration-700"></div>
            {/* Golden Energy Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-700 bg-gradient-to-br from-accent/30 to-accent-glow/30"></div>
            
            <CardHeader className="relative z-10 text-center pb-8">
              <div className="w-24 h-24 mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-golden-dark rounded-full blur-lg opacity-50 group-hover:opacity-80 transition-opacity duration-500"></div>
                <div className="relative w-full h-full border-2 border-golden-light/40 rounded-full flex items-center justify-center bg-background group-hover:border-golden-light/80 transition-all duration-500">
                  <Users className="w-10 h-10 text-accent group-hover:scale-110 transition-transform duration-500" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold mb-3 text-foreground drop-shadow-sm">
                Collective Experience
              </CardTitle>
              <CardDescription className="text-lg leading-relaxed text-muted-foreground drop-shadow-sm">
                A vibrant, community-focused journey designed for shared growth and connection
              </CardDescription>
            </CardHeader>

            <CardContent className="relative z-10 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Network className="w-5 h-5 flex-shrink-0 text-accent" />
                  <span className="font-medium">Dynamic group interaction elements</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Zap className="w-5 h-5 flex-shrink-0 text-secondary" />
                  <span className="font-medium">Energizing collective practices</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Flame className="w-5 h-5 flex-shrink-0 text-primary" />
                  <span className="font-medium">Shared motivation and inspiration</span>
                </div>
              </div>

              <Button
                onClick={(e) => {
                  console.log("🎯 Group button clicked in SessionTypeSelector");
                  e.stopPropagation();
                  onSelectType('group');
                }}
                className="w-full h-14 text-lg font-bold rounded-xl hover:scale-[1.02] transition-all duration-500 shadow-lg"
                variant="accent"
              >
                Create Group Session
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer Text */}
        <div className="text-center mt-16 animate-slideInUp" style={{ animationDelay: '0.3s' }}>
          <p className="text-muted-foreground/70 font-light">
            Both paths lead to transformation • Choose what calls to you today
          </p>
        </div>
      </div>
    </div>
  );
};